import React, { useEffect, useState } from "react";
import PageHeader from "../../../components/common/PageHeader";
import Spinner from "../../../components/common/Spinner";
import ErrorAlert from "../../../components/common/ErrorAlert";
import EmptyState from "../../../components/common/EmptyState";
import ConfirmDialog from "../../../components/common/ConfirmDialog";
import {
  fetchComplianceRegister,
  fetchInspectionRecords,
  createInspectionRecord,
  deleteInspectionRecord,
} from "../api";
import type { ComplianceRegister } from "@akfdev-com/shared/src/models/ComplianceRegister";
import type {
  InspectionRecord,
  InspectionResult,
} from "@akfdev-com/shared/src/models/InspectionRecord";

const RESULTS: InspectionResult[] = ["pass", "fail", "advisory"];

const RESULT_STYLE: Record<InspectionResult, string> = {
  pass: "bg-green-100 text-green-800",
  fail: "bg-red-100 text-red-800",
  advisory: "bg-amber-100 text-amber-800",
};

const emptyForm: Partial<InspectionRecord> = {
  complianceItemId: "",
  inspectionDate: "",
  inspector: "",
  result: "pass",
  notes: "",
};

const InspectionRecordsPage: React.FC = () => {
  const [items, setItems] = useState<InspectionRecord[]>([]);
  const [register, setRegister] = useState<ComplianceRegister[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterId, setFilterId] = useState<string>("");
  const [editing, setEditing] = useState<Partial<InspectionRecord> | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<InspectionRecord | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const [recs, reg] = await Promise.all([
        fetchInspectionRecords(filterId || undefined),
        register.length ? Promise.resolve(register) : fetchComplianceRegister(),
      ]);
      setItems(recs);
      if (!register.length) setRegister(reg);
    } catch {
      setError("Failed to load inspection records.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterId]);

  const registerName = (id: string) =>
    register.find((r) => r.id === id)?.name || id;

  const startCreate = () => {
    setEditing({ ...emptyForm, complianceItemId: filterId || "" });
  };

  const save = async () => {
    if (!editing) return;
    if (!editing.complianceItemId || !editing.inspectionDate || !editing.inspector) {
      setError("Compliance item, date and inspector are required.");
      return;
    }
    try {
      setSaving(true);
      await createInspectionRecord(editing);
      setEditing(null);
      await load();
    } catch {
      setError("Failed to save inspection record.");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteInspectionRecord(confirmDelete.inspectionId);
      setConfirmDelete(null);
      await load();
    } catch {
      setError("Failed to delete inspection record.");
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Inspection Records"
        description="History of completed inspections, results and inspectors."
        actions={
          <button
            type="button"
            onClick={startCreate}
            className="inline-flex items-center px-3 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700"
          >
            + New Inspection
          </button>
        }
      />

      <div className="bg-white shadow rounded-lg p-3 flex flex-wrap gap-3 items-end">
        <div>
          <label className="form-label">Filter by compliance item</label>
          <select
            value={filterId}
            onChange={(e) => setFilterId(e.target.value)}
            className="form-input"
          >
            <option value="">All</option>
            {register.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && <ErrorAlert message={error} onRetry={load} />}
      {loading && <Spinner />}

      {editing && (
        <div className="bg-white shadow rounded-lg p-4 space-y-4">
          <h3 className="font-semibold text-gray-900">New inspection</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="form-label">Compliance item</label>
              <select
                value={editing.complianceItemId}
                onChange={(e) =>
                  setEditing({ ...editing, complianceItemId: e.target.value })
                }
                className="form-input"
              >
                <option value="">Select…</option>
                {register.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Inspection date</label>
              <input
                type="date"
                value={editing.inspectionDate || ""}
                onChange={(e) => setEditing({ ...editing, inspectionDate: e.target.value })}
                className="form-input"
              />
            </div>
            <div>
              <label className="form-label">Inspector</label>
              <input
                type="text"
                value={editing.inspector || ""}
                onChange={(e) => setEditing({ ...editing, inspector: e.target.value })}
                className="form-input"
              />
            </div>
            <div>
              <label className="form-label">Result</label>
              <select
                value={editing.result}
                onChange={(e) =>
                  setEditing({ ...editing, result: e.target.value as InspectionResult })
                }
                className="form-input"
              >
                {RESULTS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="form-label">Notes</label>
              <textarea
                rows={2}
                value={editing.notes || ""}
                onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                className="form-input"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setEditing(null)}
              disabled={saving}
              className="px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="px-3 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      )}

      {!loading && items.length === 0 && !editing && (
        <EmptyState
          title="No inspection records"
          description="Record completed inspections to maintain an audit trail."
          actionText="+ New Inspection"
          onAction={startCreate}
        />
      )}

      {!loading && items.length > 0 && (
        <div className="bg-white shadow rounded-lg overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <Th>Date</Th>
                <Th>Compliance Item</Th>
                <Th>Inspector</Th>
                <Th>Result</Th>
                <Th>Notes</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((i) => (
                <tr key={i.inspectionId}>
                  <Td>{i.inspectionDate}</Td>
                  <Td>{registerName(i.complianceItemId)}</Td>
                  <Td>{i.inspector}</Td>
                  <Td>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${RESULT_STYLE[i.result]}`}
                    >
                      {i.result}
                    </span>
                  </Td>
                  <Td>{i.notes || ""}</Td>
                  <Td>
                    <button
                      onClick={() => setConfirmDelete(i)}
                      className="text-red-700 hover:text-red-900 text-sm font-medium"
                    >
                      Delete
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        isOpen={!!confirmDelete}
        title="Delete inspection record"
        message="Delete this inspection record? This cannot be undone."
        confirmText="Delete"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
        onClose={() => setConfirmDelete(null)}
        onConfirm={doDelete}
      />
    </div>
  );
};

const Th: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
    {children}
  </th>
);

const Td: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <td className={`px-4 py-2 text-sm text-gray-900 ${className || ""}`}>{children}</td>
);

export default InspectionRecordsPage;
